import {Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, combineLatest, Observable, Subscription} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {LocationPreference, PlacementType} from '../../../model';
import {AnalyticsService} from '../../shared/analytics.service';
import {ApiService, HttpError} from '../../shared/api.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {ModalComponent} from '../../shared/modal/modal.component';
import {NavigationService} from '../../shared/navigation.service';
import {ClientApiService} from '../client-api.service';
import {GooglePlacesComponent} from '../places-autocomplete/places-autocomplete.component';

function formatDate(date: Date) {
  return date.toISOString().slice(0, 10);
}

@Component({
  selector: 'new-role-form',
  templateUrl: './new-role-form.component.html',
  styleUrls: ['./new-role-form.component.scss'],
})
export class NewRoleFormComponent implements OnInit, OnDestroy {

  @ViewChild('newRoleFormGroup') newRoleFormGroup: FormGroup;
  @ViewChild('location') location: GooglePlacesComponent;
  @ViewChild('successModal') successModal: ModalComponent;

  @Output() success = new EventEmitter<void>();

  PlacementType = PlacementType;
  brand$ = this.AppState.brand$;
  masquerading$ = this.Auth.masquerading$;

  formDefaults: any;
  isSubmitting$ = new BehaviorSubject(false);
  locationVisible$ = new BehaviorSubject(false);
  locationInvalid$ = new BehaviorSubject(false);
  locationTouched$ = new BehaviorSubject(false);
  ratePeriod$ = new BehaviorSubject('');
  rateRange$ = new BehaviorSubject('');
  locationPreferenceOptions$ = new BehaviorSubject<LocationPreference>(LocationPreference.default) ;
  locationSub: Subscription;

  jobTitleInvalid$: Observable<boolean>;
  roleCountInvalid$: Observable<boolean>;
  minRateMissing$: Observable<boolean>;
  minRateInvalid$: Observable<boolean>;
  maxRateMissing$: Observable<boolean>;
  maxRateInvalid$: Observable<boolean>;
  startInvalid$: Observable<boolean>;
  formInvalid$: Observable<boolean>;

  formFilled$ = new BehaviorSubject(false);
  formSub: Subscription;
  skipClear = false;
  tipOpen = false;
  address = '';

  locationOptions$ = new BehaviorSubject(new Map<string, [string, {}]>([
    [LocationPreference.default, ['new-role.current-location', {}]],
    [LocationPreference.onSite, ['new-role.location-type.on-site', {}]],
    [LocationPreference.offSite, ['new-role.location-type.off-site', {}]],
    [LocationPreference.noPreference, ['new-role.location-type.no-preference', {}]]
  ]));

  constructor(private AppState: AppStateService,
              private FormBuilder: FormBuilder,
              private Auth: AuthService,
              private Translate: TranslateService,
              private Analytics: AnalyticsService,
              private Api: ClientApiService,
              private ApiService: ApiService,
              private Navigation: NavigationService,
              private Router: Router) {
  }

  ngOnInit() {
    this.initializeForm();
    this.setupValidation();

    this.ApiService.getUserProfileInfo().subscribe(profileResponse => {
      this.address = this.stringifyAddress(profileResponse.address);
      this.locationOptions$.next(new Map<string, [string, {}]>([
        [LocationPreference.default, [this.address + this.Translate.instant(
          'new-role.location-type.default'), {}]],
        [LocationPreference.onSite, ['new-role.location-type.on-site', {}]],
        [LocationPreference.offSite, ['new-role.location-type.off-site', {}]],
        [LocationPreference.noPreference, ['new-role.location-type.no-preference', {}]]
      ]));
    });
  }

  ngOnDestroy() {
    this.teardownValidation();
  }

  initializeForm(): void {
    this.formDefaults = {
      jobTitle: '',
      roleCount: '1',
      locationPreference: LocationPreference.default,
      placement: PlacementType.temp,
      minRate: '',
      maxRate: '',
      start: formatDate(new Date()),
      duration: '',
      notes: ''
    };

    this.ratePeriod$.next(this.Translate.instant('new-role.per-hour'));
    this.rateRange$.next(this.Translate.instant('new-role.rate'));

    this.newRoleFormGroup = this.FormBuilder.group({
      jobTitle: new FormControl(this.formDefaults.jobTitle,
        Validators.compose([
          Validators.required,
          Validators.minLength(1)])),
      roleCount: new FormControl(this.formDefaults.roleCount,
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{1,2}$')])),
      locationPreference: [this.formDefaults.locationPreference],
      placement: [this.formDefaults.placement],
      minRate: new FormControl(this.formDefaults.minRate,
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{1,12}$')])),
      maxRate: new FormControl(this.formDefaults.maxRate,
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{1,12}$')])),
      start: new FormControl(this.formDefaults.start,
        Validators.compose([
          Validators.required,
          Validators.pattern('^[0-9]{4}.[0-9]{2}.[0-9]{2}$'),
          Validators.minLength(10),
          Validators.maxLength(10)])),
      duration: [this.formDefaults.duration],
      notes: [this.formDefaults.notes]
    });

    this.locationVisible$.next(false);
    this.locationPreferenceOptions$.next(LocationPreference.default);
  }

  submitNewRole(): void {
    if (this.isSubmitting$.getValue() || this.masquerading$.getValue()) {
      return;
    }

    this.skipClear = false;
    this.isSubmitting$.next(true);
    const talentRequest = this.getTalentRequest();

    const talentRequestPlusId = talentRequest;
    talentRequestPlusId['id'] = this.Auth.user$.getValue().id;

    this.Analytics.trackEvent('New Role Requested: POST', {
      category: 'Action',
      request: talentRequestPlusId
    });

    this.Api.requestTalent(talentRequest).subscribe(() => {
      this.isSubmitting$.next(false);
      this.successModal.show();
      this.success.emit();
      this.Analytics.trackEvent('New Role Requested: SUCCESS', {category: 'Event', request: talentRequestPlusId});
    }, (err: HttpError) => {
      console.error('Failed to request new talent', err);
      this.Navigation.handleError(err);
      this.Analytics.trackEvent('New Role Requested: ERROR', {category: 'Error', request: talentRequestPlusId});
    });
  }

  getTalentRequest(): TalentRequest {
    let locationPreference = this.newRoleFormGroup.get('locationPreference').value;
    let location = this.location.address;
    if (LocationPreference.default === locationPreference) {
      locationPreference = LocationPreference.onSite;
      location = this.address;
    }
    return {
      role: this.newRoleFormGroup.get('jobTitle').value,
      locationPreference: locationPreference,
      location: location,
      startDate: this.newRoleFormGroup.get('start').value,
      estimatedDuration: this.newRoleFormGroup.get('duration').value,
      additionalNotes: this.newRoleFormGroup.get('notes').value,
      lowRate: this.newRoleFormGroup.get('minRate').value,
      highRate: this.newRoleFormGroup.get('maxRate').value,
      latitude: this.location.latitude,
      longitude: this.location.longitude,
      roleCount: parseInt(this.newRoleFormGroup.get('roleCount').value),
      placementType: this.newRoleFormGroup.get('placement').value
    };
  }

  setupValidation(): void {
    const jobTitle: AbstractControl = this.newRoleFormGroup.get('jobTitle');
    const roleCount: AbstractControl = this.newRoleFormGroup.get('roleCount');
    const preference: AbstractControl = this.newRoleFormGroup.get('locationPreference');
    const minRate: AbstractControl = this.newRoleFormGroup.get('minRate');
    const maxRate: AbstractControl = this.newRoleFormGroup.get('maxRate');
    const start: AbstractControl = this.newRoleFormGroup.get('start');

    this.jobTitleInvalid$ = jobTitle.valueChanges.pipe(map(() => jobTitle.errors && !jobTitle.pristine));
    this.roleCountInvalid$ = roleCount.valueChanges.pipe(map(() => roleCount.errors && !roleCount.pristine));
    this.minRateMissing$ = minRate.valueChanges.pipe(map(() => minRate.errors && minRate.errors.required && !minRate.pristine));
    this.minRateInvalid$ = minRate.valueChanges.pipe(map(() => minRate.errors && minRate.errors.pattern && !minRate.pristine));
    this.maxRateMissing$ = maxRate.valueChanges.pipe(map(() => maxRate.errors && maxRate.errors.required && !maxRate.pristine));
    this.maxRateInvalid$ = maxRate.valueChanges.pipe(map(() => maxRate.errors && maxRate.errors.pattern && !maxRate.pristine));
    this.startInvalid$ = start.valueChanges.pipe(map(() => start.errors && !start.pristine));

    this.formInvalid$ = combineLatest(
      this.locationInvalid$,
      this.newRoleFormGroup.statusChanges.pipe(startWith('')),
      (locationInvalid) => {
        return locationInvalid || this.newRoleFormGroup.invalid;
      });

    this.locationSub = preference.valueChanges.subscribe(() => {
      this.locationChanged();
    });

    this.formSub = this.newRoleFormGroup.statusChanges.subscribe(() => {
      this.formFilled$.next(this.isDefault());
    });
  }

  teardownValidation(): void {
    if (this.locationSub) {
      this.locationSub.unsubscribe();
    }
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
  }

  translateField(name): object {
    return {field: this.Translate.instant('new-role.' + name)};
  }

  isDefault(): boolean {
    for (const field in this.formDefaults) {
      if (this.formDefaults.hasOwnProperty(field)) {
        if (this.newRoleFormGroup.get(field).value !== this.formDefaults[field]) {
          return true;
        }
      }
    }
    return this.isLocationEmpty();
  }

  isLocationEmpty(): boolean {
    return (this.location && this.location.isEmpty());
  }

  clearFields(): void {
    if (this.skipClear) {
      this.skipClear = false;
      return;
    }
    this.teardownValidation();
    this.initializeForm();
    this.location.reset();
    this.setupValidation();
    this.formFilled$.next(false);
    this.locationInvalid$.next(false);
    this.locationTouched$.next(false);
  }

  locationChanged(): void {
    this.locationInvalid$.next(
      LocationPreference.onSite === this.locationPreferenceOptions$.getValue() &&
      !this.location.hasLocation);
    this.formFilled$.next(!this.location.isPristine() || this.isDefault());
    this.locationTouched$.next(!this.location.isPristine());
  }

  locationSelected($event): void {
    this.locationVisible$.next(LocationPreference.onSite === $event);
    this.locationPreferenceOptions$.next($event);
    this.locationChanged();
  }

  placementChanged(): void {
    if (PlacementType.perm === this.newRoleFormGroup.get('placement').value) {
      this.ratePeriod$.next(this.Translate.instant('new-role.per-year'));
      this.rateRange$.next(this.Translate.instant('new-role.salary'));
    } else {
      this.ratePeriod$.next(this.Translate.instant('new-role.per-hour'));
      this.rateRange$.next(this.Translate.instant('new-role.rate'));
    }
  }

  anotherNewRole(): void {
    this.smoothScrollToTop();
    this.skipClear = true;
    this.successModal.hide();
    this.Analytics.trackEvent('New Role Requested: Another', {category: 'Action'});
  }

  gotoOpenPositions(): void {
    this.successModal.hide();
    this.Router.navigate(['/positions', 'open']);
  }

  stringifyAddress(address: Profile.MailingAddress): string {
    if (address.country === 'US') {
      return address.city + ', ' + address.state;
    }
    return address.city + ', ' + address.country;
  }

  smoothScrollToTop() {
    const scrollToTop = window.setInterval(() => {
      const pos = window.pageYOffset;
      if (pos > 0) {
        window.scrollTo(0, pos - 20); // how far to scroll on each step
      } else {
        window.clearInterval(scrollToTop);
      }
    }, 16);
  }
}
